<template>
  <div class="page-validacao">
    <div class="page-header">
      <h1>Validação Pública de Documento</h1>
      <p>
        Informe o PIN de validação do documento para consultar sua autenticidade
        e visualizar o arquivo vinculado.
      </p>
    </div>

    <div class="card filtro-card">
      <div class="row row-gap">
        <div class="field field-pin">
          <label>PIN de validação</label>
          <input
            v-model="pinInformado"
            type="text"
            placeholder="Ex.: 03000208-2026-A1B2"
            @keyup.enter="validarDocumento"
          />
        </div>

        <div class="field field-acoes">
          <label>&nbsp;</label>
          <button class="btn-primary" @click="validarDocumento" :disabled="loading">
            {{ loading ? 'Validando...' : 'Validar documento' }}
          </button>
        </div>

        <div class="field field-acoes" v-if="documento">
          <label>&nbsp;</label>
          <button class="btn-secondary" @click="limparConsulta">
            Nova consulta
          </button>
        </div>
      </div>

      <div v-if="mensagem" class="alerta" :class="tipoMensagem">
        {{ mensagem }}
      </div>
    </div>

    <div v-if="documento" class="resultado">
      <div class="card status-card">
        <div class="status-topo">
          <div>
            <h2>{{ documento.titulo }}</h2>
            <p class="subtitulo">
              Documento nº {{ documento.numeroDocumento }} • Referência {{ documento.referencia }}
            </p>
          </div>

          <div class="status-badge" :class="documento.status">
            {{ descricaoStatus(documento.status) }}
          </div>
        </div>

        <div class="grid-info">
          <div class="info-item">
            <span class="info-label">PIN</span>
            <span class="info-value">{{ documento.pin }}</span>
          </div>

          <div class="info-item">
            <span class="info-label">Estabelecimento</span>
            <span class="info-value">{{ documento.estabelecimento }}</span>
          </div>

          <div class="info-item">
            <span class="info-label">SIM</span>
            <span class="info-value">{{ documento.sim }}</span>
          </div>

          <div class="info-item">
            <span class="info-label">Tipo do documento</span>
            <span class="info-value">{{ documento.tipoDocumento }}</span>
          </div>

          <div class="info-item">
            <span class="info-label">Emitido em</span>
            <span class="info-value">{{ documento.dataEmissao }}</span>
          </div>

          <div class="info-item">
            <span class="info-label">Hash do arquivo</span>
            <span class="info-value info-hash">{{ documento.hashArquivo }}</span>
          </div>
        </div>

        <div class="observacao-validacao">
          <strong>Validação:</strong>
          Este documento foi localizado no sistema e seu registro encontra-se
          com integridade preservada, conforme hash armazenado e assinaturas vinculadas.
        </div>
      </div>

      <div class="card assinaturas-card">
        <h3>Assinaturas eletrônicas vinculadas</h3>

        <table class="table-default">
          <thead>
            <tr>
              <th>Assinante</th>
              <th>Perfil</th>
              <th>Data/Hora</th>
              <th>Autenticação</th>
              <th>IP</th>
              <th>Hash da assinatura</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(assinatura, index) in documento.assinaturas" :key="index">
              <td>{{ assinatura.nome }}</td>
              <td>{{ assinatura.perfil }}</td>
              <td>{{ assinatura.dataHora }}</td>
              <td>{{ assinatura.autenticacao }}</td>
              <td>{{ assinatura.ip }}</td>
              <td class="td-hash">{{ assinatura.hash }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="card arquivo-card">
        <div class="arquivo-topo">
          <h3>Documento anexado</h3>

          <a
            v-if="documento.urlDocumento"
            :href="documento.urlDocumento"
            target="_blank"
            rel="noopener noreferrer"
            class="btn-link"
          >
            Abrir em nova guia
          </a>
        </div>

        <div class="arquivo-preview">
          <iframe
            v-if="documento.urlDocumento"
            :src="documento.urlDocumento"
            title="Pré-visualização do documento"
          ></iframe>

          <div v-else class="sem-arquivo">
            Nenhum arquivo disponível para visualização.
          </div>
        </div>
      </div>

      <div class="card historico-card">
        <h3>Informações complementares</h3>

        <div class="grid-info">
          <div class="info-item">
            <span class="info-label">Situação do registro</span>
            <span class="info-value">{{ descricaoStatus(documento.status) }}</span>
          </div>

          <div class="info-item">
            <span class="info-label">Origem</span>
            <span class="info-value">{{ documento.origem }}</span>
          </div>

          <div class="info-item">
            <span class="info-label">Versão do sistema</span>
            <span class="info-value">{{ documento.versaoSistema }}</span>
          </div>

          <div class="info-item">
            <span class="info-label">Sessões registradas</span>
            <span class="info-value">{{ documento.sessoes }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';

const pinInformado = ref('');
const loading = ref(false);
const mensagem = ref('');
const tipoMensagem = ref('info');
const documento = ref(null);

function descricaoStatus(status) {
  if (status === 'valido') return 'Válido';
  if (status === 'cancelado') return 'Cancelado';
  if (status === 'invalido') return 'Inválido';
  return 'Não identificado';
}

function limparConsulta() {
  pinInformado.value = '';
  mensagem.value = '';
  tipoMensagem.value = 'info';
  documento.value = null;
}

/*
  Aqui está mockado para a POC.
  Depois você troca por chamada real da API, por exemplo:
  GET /publico/documentos/validar?pin=...
*/
async function validarDocumento() {
  mensagem.value = '';
  documento.value = null;

  if (!pinInformado.value.trim()) {
    tipoMensagem.value = 'erro';
    mensagem.value = 'Informe o PIN de validação.';
    return;
  }

  loading.value = true;

  try {
    await new Promise(resolve => setTimeout(resolve, 700));

    const pin = pinInformado.value.trim().toUpperCase();

    if (pin === '03000208-2026-A1B2') {
      documento.value = {
        pin: '03000208-2026-A1B2',
        titulo: 'PLA 08 - Relatório de Ações Corretivas (RAC)',
        numeroDocumento: '003-010/2025',
        referencia: '010/2025',
        tipoDocumento: 'Laudo / Homologação',
        estabelecimento: 'Casa das Carnes',
        sim: '1',
        dataEmissao: '08/03/2026 10:57',
        status: 'valido',
        hashArquivo: 'e29f45e0deb7cf6c0665da38718919d6',
        origem: 'SGSIM - Serviço de Inspeção Municipal',
        versaoSistema: '1.0.20',
        sessoes: '4111 / 4112',
        /*
          Na POC você pode apontar para um PDF público/mockado.
          Depois isso pode vir da API:
          urlDocumento: response.data.urlDocumento
        */
        urlDocumento: '/documentos-exemplo/pla08-validacao.pdf',
        assinaturas: [
          {
            nome: 'RESPONSAVEL TECNICO 1 (ID 55)',
            perfil: 'Responsável Técnico',
            dataHora: '08/03/2026 10:56',
            autenticacao: 'Senha pessoal via Aplicativo',
            ip: '192.168.5.14',
            hash: 'e29f45e0deb7cf6c0665da38718919d6'
          },
          {
            nome: 'ADMIN CASA DAS CARNES (ID 56)',
            perfil: 'Responsável pelo Estabelecimento',
            dataHora: '08/03/2026 10:57',
            autenticacao: 'Senha pessoal via Aplicativo',
            ip: '192.168.5.14',
            hash: 'f5a0c4c9f9fbe6e318c426e27dd57d2b'
          }
        ]
      };

      tipoMensagem.value = 'sucesso';
      mensagem.value = 'Documento localizado e validado com sucesso.';
      return;
    }

    tipoMensagem.value = 'erro';
    mensagem.value = 'PIN não localizado ou documento indisponível para validação.';
  } catch (error) {
    tipoMensagem.value = 'erro';
    mensagem.value = 'Ocorreu um erro ao validar o documento.';
    console.error(error);
  } finally {
    loading.value = false;
  }
}
</script>

<style scoped>
.page-validacao {
  padding: 20px;
  background: #f5f7fb;
  min-height: 100%;
}

.page-header {
  margin-bottom: 18px;
}

.page-header h1 {
  margin: 0 0 6px 0;
  font-size: 26px;
  color: #243447;
}

.page-header p {
  margin: 0;
  color: #5b6b7b;
}

.card {
  background: #ffffff;
  border: 1px solid #dfe6ee;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(31, 45, 61, 0.04);
}

.row {
  display: flex;
  flex-wrap: wrap;
}

.row-gap {
  gap: 12px;
}

.field {
  display: flex;
  flex-direction: column;
}

.field label {
  font-size: 13px;
  font-weight: 600;
  color: #425466;
  margin-bottom: 6px;
}

.field input {
  height: 40px;
  border: 1px solid #cfd8e3;
  border-radius: 8px;
  padding: 0 12px;
  outline: none;
  font-size: 14px;
}

.field input:focus {
  border-color: #4e7cff;
  box-shadow: 0 0 0 3px rgba(78, 124, 255, 0.12);
}

.field-pin {
  flex: 1 1 320px;
}

.field-acoes {
  flex: 0 0 auto;
  min-width: 180px;
}

.btn-primary,
.btn-secondary,
.btn-link {
  height: 40px;
  border-radius: 8px;
  padding: 0 16px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.btn-primary {
  border: none;
  background: #1f6feb;
  color: #fff;
}

.btn-primary:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn-secondary {
  border: 1px solid #cfd8e3;
  background: #fff;
  color: #344054;
}

.btn-link {
  border: 1px solid #cfd8e3;
  background: #fff;
  color: #1f6feb;
}

.alerta {
  margin-top: 14px;
  padding: 12px 14px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
}

.alerta.sucesso {
  background: #ecfdf3;
  color: #027a48;
  border: 1px solid #abefc6;
}

.alerta.erro {
  background: #fef3f2;
  color: #b42318;
  border: 1px solid #fecdca;
}

.alerta.info {
  background: #eff8ff;
  color: #175cd3;
  border: 1px solid #b2ddff;
}

.status-topo {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 14px;
}

.status-topo h2 {
  margin: 0;
  font-size: 22px;
  color: #243447;
}

.subtitulo {
  margin: 4px 0 0 0;
  color: #667085;
}

.status-badge {
  padding: 8px 14px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
}

.status-badge.valido {
  background: #ecfdf3;
  color: #027a48;
}

.status-badge.cancelado {
  background: #fff6ed;
  color: #c4320a;
}

.status-badge.invalido {
  background: #fef3f2;
  color: #b42318;
}

.grid-info {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
}

.info-item {
  background: #f8fafc;
  border: 1px solid #e6edf5;
  border-radius: 10px;
  padding: 12px;
}

.info-label {
  display: block;
  font-size: 12px;
  color: #667085;
  margin-bottom: 4px;
}

.info-value {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #1d2939;
  word-break: break-word;
}

.info-hash {
  font-family: Consolas, Monaco, monospace;
  font-size: 12px;
}

.observacao-validacao {
  margin-top: 14px;
  padding: 12px 14px;
  background: #f8fafc;
  border-left: 4px solid #1f6feb;
  border-radius: 8px;
  color: #344054;
}

.assinaturas-card h3,
.arquivo-card h3,
.historico-card h3 {
  margin-top: 0;
  margin-bottom: 14px;
  color: #243447;
}

.table-default {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.table-default th,
.table-default td {
  border: 1px solid #e4e7ec;
  padding: 10px;
  text-align: left;
  vertical-align: top;
}

.table-default thead th {
  background: #f9fafb;
  color: #344054;
  font-weight: 700;
}

.td-hash {
  font-family: Consolas, Monaco, monospace;
  font-size: 12px;
  word-break: break-word;
}

.arquivo-topo {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
}

.arquivo-preview {
  border: 1px solid #dfe6ee;
  border-radius: 10px;
  overflow: hidden;
  background: #f9fafb;
}

.arquivo-preview iframe {
  width: 100%;
  height: 760px;
  border: none;
  background: #fff;
}

.sem-arquivo {
  padding: 30px;
  text-align: center;
  color: #667085;
}

@media (max-width: 900px) {
  .status-topo,
  .arquivo-topo {
    flex-direction: column;
    align-items: stretch;
  }

  .arquivo-preview iframe {
    height: 520px;
  }
}
</style>